import { Grid } from "@material-ui/core";
import Table from "components/Table";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Cell } from "react-table";
import { RootState } from "redux/rootReducer";
import { getReportDateSelector } from "redux/user/user";
import { loadPrivateEquityCapitalCall } from "redux/privateEquityCapitalCall/privateEquityCapitalCallFunctions";
import { PrivateEquityCapitalCall } from "types/PrivateEquityCapitalCall";
import { asEur } from "utility/numbers";
import { dateToString } from "../../../utility/date";
import moment from "moment";
import BlobTable, { BlobLane } from "../../../components/BlobTable";

const PrivateEquityCapitalCallsTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );
  const reportDate = useSelector(getReportDateSelector);

  const privateEquityCapitalCall = useSelector(
    (state: RootState) => state.privateEquityCapitalCall.all?.data ?? []
  );
  const loading = useSelector(
    (state: RootState) => state.privateEquityCapitalCall.all?.loading
  );

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(loadPrivateEquityCapitalCall(reportControlId, reportDate));
    }
  }, [dispatch, reportControlId, reportDate]);

  const footerItem = privateEquityCapitalCall.find(
    (x) => x.Kategoria === "Yhteensä"
  );

  const privateEquityCapitalCallColumns = [
    {
      Header: t("fund"),
      accessor: "Alakategoria",
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Footer: footerItem?.Alakategoria ?? "",
    },
    {
      Header: t("fund_start_date"),
      accessor: "Alkupvm",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, Date>) =>
        dateToString(cell.value),
      minWidth: 100,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: dateToString(footerItem?.Alkupvm),
    },
    {
      Header: t("maturity"),
      accessor: "Maturiteetti",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        `${cell.value} ${t("years")}`,
      whiteSpace: "nowrap",
      minWidth: 100,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: "",
    },
    {
      Header: t("commitment"),
      accessor: "Sitoumus",
      whiteSpace: "nowrap",
      paddingRight: "10px",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(cell.value, undefined, true),
      minWidth: 100,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(footerItem?.Sitoumus, undefined, true),
    },
    {
      Header: t("uninvited_capital"),
      accessor: "Kutsumaton",
      whiteSpace: "nowrap",
      paddingRight: "10px",
      minWidth: 100,
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(cell.value, undefined, true),
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(footerItem?.Kutsumaton, undefined, true),
      separator: true,
    },
    {
      Header: moment(reportDate).year().toString(),
      accessor: "Vuosi1",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi1 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(1, "years").year().toString(),
      accessor: "Vuosi2",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi2 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(2, "years").year().toString(),
      accessor: "Vuosi3",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi3 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(3, "years").year().toString(),
      accessor: "Vuosi4",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi4 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(4, "years").year().toString(),
      accessor: "Vuosi5",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi5 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(5, "years").year().toString(),
      accessor: "Vuosi6",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi6 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(6, "years").year().toString(),
      accessor: "Vuosi7",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi7 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(7, "years").year().toString(),
      accessor: "Vuosi8",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi8 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(8, "years").year().toString(),
      accessor: "Vuosi9",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi9 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(9, "years").year().toString(),
      accessor: "Vuosi10",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi10 : undefined,
        undefined,
        true
      ),
    },
    {
      Header: moment(reportDate).add(10, "years").year().toString(),
      accessor: "Vuosi11",
      whiteSpace: "nowrap",
      Cell: (cell: Cell<PrivateEquityCapitalCall, number>) =>
        asEur(-cell.value, undefined, true),
      minWidth: 70,
      textAlign: "right",
      fontFamily: "Lato light",
      Footer: asEur(
        footerItem !== undefined ? -footerItem.Vuosi11 : undefined,
        undefined,
        true
      ),
    },
  ];

  const timespan = 11;
  const years = Array(timespan)
    .fill(moment(reportDate).year())
    .map((year, i) => (year + i).toString());

  const reportYear = moment(reportDate).year();
  const yearIndices = [
    "Vuosi1",
    "Vuosi2",
    "Vuosi3",
    "Vuosi4",
    "Vuosi5",
    "Vuosi6",
    "Vuosi7",
    "Vuosi8",
    "Vuosi9",
    "Vuosi10",
    "Vuosi11",
  ];
  const privateEquityCapitalCallData = privateEquityCapitalCall
    .filter((x) => x.Kategoria !== "Yhteensä") // Get rid of the included sum row
    .map((row) => {
      yearIndices.forEach((key, offset) => {
        if (
          // Replace nulls with zeros in cells where the fund is active
          // @ts-ignore
          row[key] === null &&
          moment(row.Alkupvm).year() <= reportYear + offset &&
          moment(row.Alkupvm).year() + row.Maturiteetti >= reportYear + offset
        ) {
          // @ts-ignore
          row[key] = 0;
        }
      });
      return row;
    });

  const blobLanes = privateEquityCapitalCallData.map((row): BlobLane => {
    return {
      title: row.Alakategoria,
      begin: moment(row.Alkupvm).year() - reportYear,
      length: row.Maturiteetti,
      values: [
        row.Vuosi1,
        row.Vuosi2,
        row.Vuosi3,
        row.Vuosi4,
        row.Vuosi5,
        row.Vuosi6,
        row.Vuosi7,
        row.Vuosi8,
        row.Vuosi9,
        row.Vuosi10,
        row.Vuosi11,
      ],
      commitment: row.Sitoumus,
      called: row.Sitoumus - row.Kutsumaton,
    };
  });

  return (
    <Grid item container>
      <Grid item sm={12}>
        <h3 style={{ color: "#444443" }}>{t("cashflow_forecast")}</h3>
      </Grid>
      <Grid item sm={12}>
        <BlobTable headings={years} showTotals={true} lanes={blobLanes} />
      </Grid>
      <Grid item style={{ overflowY: "hidden" }}>
        <Table
          data={privateEquityCapitalCallData}
          columns={privateEquityCapitalCallColumns}
          hasFooter
          shadeNulls
          loading={loading}
        />
      </Grid>
    </Grid>
  );
};

export default React.memo(PrivateEquityCapitalCallsTable);
